import { Menu as BaseMenu } from '@base-ui/react/menu';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { Button } from '../button/button.tsx';
import { Menu, MenuContent, MenuTrigger } from '../menu/menu.tsx';
import { ColorDot } from './color-dot.tsx';
import type { TypePillProps } from './type-pill.tsx';

export interface TypeColorPickerProps {
  /** The colours on offer, in the order they are shown. */
  options: readonly TypePillProps['type'][];
  value: TypePillProps['type'];
  onValueChange: (value: TypePillProps['type']) => void;
  /** What each colour is called — a colour has no text of its own. */
  labelOf: (value: TypePillProps['type']) => string;
  /** A colour that may be read but not changed. */
  disabled?: boolean;
  /* Names the set of colours for screen readers; it has no visible label. */
  'aria-label': string;
  className?: string;
}

/**
 * A colour, out of the ones the design system has.
 *
 * A fixed palette rather than a colour wheel: every pill has to stay readable
 * in both themes. Closed by default — a colour is picked once and then read
 * many times, so the row it sits in shows the answer rather than the question.
 *
 * The trigger is named by what is chosen, the way every other menu button in
 * the system is; the palette behind it is a radio group, so the colour in use
 * is announced as the selected one instead of merely looking outlined.
 */
export function TypeColorPicker({
  options,
  value,
  onValueChange,
  labelOf,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: TypeColorPickerProps) {
  return (
    <Menu>
      <MenuTrigger
        data-slot='type-color-picker'
        disabled={disabled}
        className={className}
        render={<Button size='sm' variant='quiet' />}
      >
        <ColorDot type={value} />
        {labelOf(value)}
        <ChevronDown aria-hidden='true' className='size-2.5 stroke-3 text-ink-muted' />
      </MenuTrigger>
      {/* End-aligned and no wider than the swatches: it belongs to the trigger, not to the row. */}
      <MenuContent align='end' className='min-w-0'>
        <BaseMenu.RadioGroup
          aria-label={ariaLabel}
          value={value}
          onValueChange={(next: TypePillProps['type']) => onValueChange(next)}
          className='flex gap-1'
        >
          {options.map((option) => (
            <BaseMenu.RadioItem
              key={option}
              value={option}
              closeOnClick
              aria-label={labelOf(option)}
              className={cn(
                'flex size-7 cursor-pointer items-center justify-center rounded-sm bg-fill',
                'outline-none transition-colors hover:bg-line data-highlighted:bg-line',
                /* Inset, so the chosen swatch keeps the size of every other one. */
                'data-checked:ring-2 data-checked:ring-ink data-checked:ring-inset',
              )}
            >
              <ColorDot type={option} size='lg' />
            </BaseMenu.RadioItem>
          ))}
        </BaseMenu.RadioGroup>
      </MenuContent>
    </Menu>
  );
}
